import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexXAxis,
  ApexPlotOptions,
  ApexStroke,
  ApexTitleSubtitle,
  ApexYAxis,
  ApexTooltip,
  ApexFill,
  ApexLegend,
} from "ng-apexcharts";

type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  tooltip: ApexTooltip;
  fill: ApexFill;
  legend: ApexLegend;
};

@Component({
  selector: "lib-stacked-bar-chart",
  templateUrl: "./stacked-bar-chart.component.html",
})
export class StackedBarChartComponent implements OnInit {
  @Input() horizontal: boolean;
  @Input() stackType: any;
  @Input() info: any;
  @Input() categorias: any;
  @Input() valorMaximo: number;
  @Input() colors: any;
  @Input() showDatalabels: boolean;
  @Input() nombre: string;

  public smallScreen: boolean;
  private readonly _BREAKPOINT_SIZE_SCREEN = 1375;

  @Output() rendered = new EventEmitter<boolean>();

  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  constructor(private readonly _translate: TranslateService) {
    this.smallScreen =
      window.innerWidth <= this._BREAKPOINT_SIZE_SCREEN ? true : false;
  }

  @HostListener("window:resize")
  onWindowResize() {
    console.log("window:resize");
    this.smallScreen =
      window.innerWidth <= this._BREAKPOINT_SIZE_SCREEN ? true : false;
    this.drawChart();
  }

  ngOnInit() {
    this.drawChart();
  }

  private drawChart() {
    const filteredData = this.info.map((serie: any) => {
      return {
        ...serie,
        data: serie.data.map((value: any) => (value === 0 ? null : value)),
      };
    });

    this.chartOptions = {
      series: filteredData,
      chart: {
        type: "bar",
        height: this.horizontal
          ? (this.categorias.length + 2) * 50
          : this.smallScreen
          ? 500
          : 800,
        width: this.horizontal
          ? this.smallScreen
            ? 1000
            : 1100
          : this.smallScreen
          ? 500
          : 600,
        stacked: true,
        stackType: this.stackType,
        fontFamily: "medium",
        animations: {
          enabled: false,
        },
        redrawOnWindowResize: true,
      },
      plotOptions: {
        bar: {
          horizontal: this.horizontal,
          hideZeroBarsWhenGrouped: true,
        },
      },
      title: {},
      stroke: {
        width: 1,
        colors: ["#fff"],
      },
      xaxis: {
        type: "category",
        categories: this.categorias,
        min: this.horizontal ? 0 : undefined,
        max:
          this.stackType === "normal"
            ? this.horizontal
              ? this.valorMaximo
              : undefined
            : 100,
        labels: {
          style: {
            fontSize: "11.8px",
            fontFamily: "medium",
            colors: "#184d39",
          },
          formatter: (val) => {
            return isNaN(+val)
              ? val.toString()
              : +val >= 2000 && +val <= 2200
              ? parseInt(val.toString()).toString()
              : Number(val).toLocaleString(this._translate.getDefaultLang(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
          },
        },
      },
      yaxis: {
        title: {
          text: undefined,
        },
        min: !this.horizontal ? 0 : undefined,
        max:
          this.stackType === "normal"
            ? this.horizontal
              ? undefined
              : this.valorMaximo
            : 100,
        labels: {
          style: {
            fontSize: "11.8px",
            fontFamily: "medium",
            colors: "#184d39",
          },
          formatter: (val, opts) => {
            return isNaN(+val)
              ? val.toString()
              : +val >= 2000 && +val <= 2200
              ? parseInt(val.toString()).toString()
              : Number(val).toLocaleString(this._translate.getDefaultLang(), {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                });
          },
        },
      },
      tooltip: {
        marker: {
          show: false,
        },
        y: {
          formatter: (val, opts) => {
            let dataLabel;
            isNaN(+val)
              ? (dataLabel = val.toString() + +" " + this.nombre)
              : (dataLabel =
                  Number(val).toLocaleString(this._translate.getDefaultLang(), {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }) +
                  " " +
                  this.nombre);
            return dataLabel;
          },
        },
      },
      fill: {
        opacity: 1,
        colors: this.colors,
      },
      legend: {
        position: "top",
        horizontalAlign: "center",
        width: this.horizontal
          ? this.smallScreen
            ? 850
            : 900
          : this.smallScreen
          ? 350
          : 400,
        offsetX: this.smallScreen ? 50 : 70,
        offsetY: 0,
        markers: {
          fillColors: this.colors,
        },
        onItemClick: {
          toggleDataSeries: false,
        },
        onItemHover: {
          highlightDataSeries: false,
        },
      },
      dataLabels: {
        enabled: this.showDatalabels,
        dropShadow: {
          enabled: false,
        },
        formatter: (val: any, opts) => {
          let calculo;

          if (opts.w.globals.maxY <= 1000) {
            calculo = 0.03;
          } else if (
            opts.w.globals.maxY > 1000 &&
            opts.w.globals.maxY <= 100000
          ) {
            calculo = 0.05;
          } else if (opts.w.globals.maxY > 100000) {
            calculo = 0.07;
          }

          if (this.stackType === "normal") {
            if (+val / opts.w.globals.maxY >= calculo) {
              return Math.round(+val).toLocaleString(
                this._translate.getDefaultLang(),
                { minimumFractionDigits: 0, maximumFractionDigits: 0 }
              );
            } else {
              return "";
            }
          } else {
            if (+val / opts.w.globals.maxY >= 0.04) {
              return Math.round(+val).toString() + "%";
            } else {
              return "";
            }
          }
        },
      },
    };
  }

  public sendEmit(rendered: boolean) {
    this.rendered.emit(rendered);
  }
}
